/**
 * Enhanced Button Component with improved type safety
 * 
 * Updates all button components to use consistent styling based on the design system.
 * Implements proper variants, sizes, and accessibility features with standardized typing.
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/utils/cn";
import { WithIconProps } from "@/types/component-types";

/**
 * Button variant options
 */
export type ButtonVariant = 
  | "default" 
  | "destructive" 
  | "outline" 
  | "secondary" 
  | "ghost" 
  | "link" 
  | "accent";

/**
 * Button size options
 */
export type ButtonSize = "default" | "sm" | "lg" | "icon";

const buttonVariants = cva(
  "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none ring-offset-background",
  {
    variants: {
      variant: {
        default: "bg-blue-600 text-white hover:bg-blue-700",
        destructive:
          "bg-red-600 text-white hover:bg-red-700",
        outline:
          "border border-gray-300 bg-white hover:bg-gray-50 text-gray-700",
        secondary:
          "bg-blue-100 text-blue-900 hover:bg-blue-200",
        ghost: "hover:bg-gray-100 text-gray-700",
        link: "underline-offset-4 hover:underline text-blue-600",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

/**
 * Extended button props interface with icon support
 */
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants>,
    WithIconProps {
  /** Whether to render as a child component using Radix Slot */
  asChild?: boolean;
  /** Loading state of the button */
  loading?: boolean;
  /** Text to display when button is in loading state */
  loadingText?: string;
}

/**
 * Button Component
 * 
 * @param {ButtonProps} props - Component props
 * @returns {JSX.Element} Rendered button component
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ 
    className, 
    variant, 
    size, 
    asChild = false, 
    icon,
    iconPosition = "left",
    iconClassName,
    loading = false,
    loadingText,
    children,
    disabled,
    ...props 
  }, ref): JSX.Element => {
    const Comp = asChild ? Slot : "button";
    const isDisabled = disabled || loading;

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isDisabled}
        {...props}
      >
        {loading ? (
          <>
            <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
            {loadingText || children}
          </>
        ) : (
          <>
            {icon && iconPosition === "left" && (
              <span className={cn("mr-2", iconClassName)}>{icon}</span>
            )}
            {children}
            {icon && iconPosition === "right" && (
              <span className={cn("ml-2", iconClassName)}>{icon}</span>
            )}
          </>
        )}
      </Comp>
    );
  }
);

Button.displayName = "Button";

export { Button, buttonVariants };